import React, { useState, useEffect } from 'react';
import { FaChevronDown, FaChevronUp, FaHistory, FaFileAlt, FaTrashAlt, FaLock, FaSave } from 'react-icons/fa';
import { LuImage, LuFileText } from 'react-icons/lu';

const formatToThaiBE = (dateStr) => {
    if (!dateStr || dateStr.trim() === "" || dateStr === "null" || dateStr === "-") return "-";
    
    const parts = dateStr.split('-');
    if (parts.length === 3) {
        const yearAD = parseInt(parts[0], 10);
        const yearBE = yearAD + 543; // หักดิบบวก 543 เปลี่ยนเป็น พ.ศ.
        return `${parts[2]}/${parts[1]}/${yearBE}`; // จัดฟอร์แมตใหม่เป็น DD/MM/YYYY
    }
    return dateStr;
};

// 🎯 ฟังก์ชันล้างพิษคำว่า "ปี" เบิ้ลซ้ำ (ฆ่าบั๊ก ปี ปี)
const formatCleanAge = (ageStr) => {
    if (!ageStr) return '';
    // สกัดคำว่า "ปี" ที่อาจจะติดมาจาก Odoo ออกให้หมดก่อน แล้วค่อยแปะกลับไปตัวเดียวนิ่งๆ
    const cleanNumber = ageStr.toString().replace(/ปี/g, '').trim();
    return ` (${cleanNumber} ปี)`;
};

function CollapsiblePatientInfo({ 
    patientContext, 
    currentVisit,
    selectedTemplate,
    templates,
    isReadOnly,
    isSaving,
    lastSaved,
    onShowVisitHistory,
    onShowTemplates,
    onLoadTemplate,
    onClearAll,
    onConfirmVisit,
    onExportPNG,
    onExportPDF,
    showTemplates,
    zoomLevel,
    onZoomIn,
    onZoomOut,
    onResetZoom
}) {
    // Start expanded by default so staff can see all options immediately
    const [isExpanded, setIsExpanded] = useState(true);

    const toggleExpanded = () => {
        setIsExpanded(!isExpanded);
    };

    // Helper style for grid rows
    const rowStyle = {
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
        gap: '10px',
        marginBottom: '10px'
    };

    // Compact View (Default) - Just a thin bar
    if (!isExpanded) {
        return (
            <div 
                onClick={toggleExpanded}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    toggleExpanded();
                }}
                style={{
                    padding: '10px 20px',
                    backgroundColor: '#2196F3',
                    color: 'white',
                    borderRadius: '8px',
                    marginBottom: '15px',
                    cursor: 'pointer',
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                    transition: 'all 0.3s ease',
                    userSelect: 'none',
                    WebkitUserSelect: 'none',
                    WebkitTapHighlightColor: 'rgba(33, 150, 243, 0.3)',
                    MozUserSelect: 'none',
                    msUserSelect: 'none',
                    touchAction: 'manipulation'
                }}
            >
                <div style={{ 
                    fontSize: '16px', 
                    fontWeight: 'bold',
                    flex: 1
                }}>
                    👤 {patientContext.name} - {patientContext.x_studio_class || '---'}
                    {selectedTemplate && (
                        /* FIXED: Added notranslate to stop "Basil Leaf" error */
                        <span 
                            className="notranslate" 
                            translate="no" 
                            style={{ marginLeft: '15px', fontSize: '14px' }}
                        >
                            📋 {selectedTemplate.displayName}
                        </span>
                    )}
                </div>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    {isSaving && <span style={{ fontSize: '14px' }}>💾 Saving...</span>}
                    {!isSaving && lastSaved && (
                        <span className="saved-status-blink" style={{ fontSize: '14px' }}>
                            ✅ บันทึกสำเร็จ
                        </span>
                    )}
                    <FaChevronDown style={{ fontSize: '16px' }} />
                </div>
            </div>
        );
    }

    // Expanded View - Contains EVERYTHING
    return (
        <div 
            style={{
                padding: '20px',
                backgroundColor: '#f5f5ff',
                borderRadius: '8px',
                marginBottom: '15px',
                border: '2px solid #2196F3',
                boxShadow: '0 2px 6px rgba(0,0,0,0.1)',
                transition: 'all 0.3s ease'
            }}
        >
            {/* ========== SECTION 1: PATIENT INFO ========== */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: '15px',
                paddingBottom: '12px',
                borderBottom: '2px solid #2196F3'
            }}>
                <h2 style={{ 
                    margin: 0, 
                    fontSize: '24px',
                    color: '#2196F3'
                }}>
                    👤 {patientContext.name} - {patientContext.x_studio_class || '---'}
                </h2>
                <button
                    onClick={toggleExpanded}
                    style={{
                        padding: '8px 16px',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        fontSize: '14px',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        touchAction: 'manipulation',
                        WebkitTapHighlightColor: 'rgba(33, 150, 243, 0.3)'
                    }}
                >
                    <FaChevronUp /> Collapse
                </button>
            </div>

            {/* Patient Details Container */}
            <div style={{ fontSize: '14px', marginBottom: '20px' }}>
                
                {/* === ROW 1: HN | Appointment | Branch | Doctor === */}
                <div style={rowStyle}>
                    {/* HN */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>📋 HN:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>{patientContext.branchHN}</div>
                    </div>

                    {/* Appointment Date */}
                    {patientContext.appointmentDate && (
                        <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                            <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>📅 Appointment:</div>
                            <div style={{ fontWeight: 'bold', color: '#333' }}>
                                {new Date(patientContext.appointmentDate).toLocaleDateString('en-GB', {
                                    day: 'numeric', month: 'long', year: 'numeric'
                                })}
                            </div>
                        </div>
                    )}

                    {/* Branch */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>🏢 Branch:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>{patientContext.branch}</div>
                    </div>

                    {/* Doctor */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>👨‍⚕️ Doctor:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                            {/* 🚀 ซ่อมประวัติศาสตร์ปนเปื้อน: ถ้าประวัติเก่ามีคำว่า V Clinic หรือรหัส VC ให้มองข้ามแล้วเอาชื่อหมอปัจจุบันมาโชว์แทน */}
                            {(currentVisit && currentVisit.doctor_name && !currentVisit.doctor_name.includes('V Clinic') && !currentVisit.doctor_name.startsWith('VC')) 
                                ? currentVisit.doctor_name 
                                : (patientContext.doctor || '-')}
                        </div>
                    </div>
                </div>

                {/* === ROW 2: Weight | Height | Illness | Drug All | Food All === */}
                <div style={rowStyle}>
                    {/* Weight (Blue Highlight) */}
                    <div style={{ 
                        padding: '8px', 
                        backgroundColor: patientContext.x_studio_weight ? '#e3f2fd' : 'white', 
                        borderRadius: '6px', 
                        border: patientContext.x_studio_weight ? '1px solid #2196f3' : '1px solid #e0e0e0'
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>⚖️ Weight:</div>
                        <div style={{ fontWeight: 'bold', color: patientContext.x_studio_weight ? '#1565c0' : '#333' }}>
                            {patientContext.x_studio_weight ? `${patientContext.x_studio_weight} kg` : '-'}
                        </div>
                    </div>

                    {/* Height (Blue Highlight) */}
                    <div style={{ 
                        padding: '8px', 
                        backgroundColor: patientContext.x_studio_height ? '#e3f2fd' : 'white', 
                        borderRadius: '6px', 
                        border: patientContext.x_studio_height ? '1px solid #2196f3' : '1px solid #e0e0e0'
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>📏 Height:</div>
                        <div style={{ fontWeight: 'bold', color: patientContext.x_studio_height ? '#1565c0' : '#333' }}>
                            {patientContext.x_studio_height ? `${patientContext.x_studio_height} cm` : '-'}
                        </div>
                    </div>

                    {/* Personal Illness (Orange Highlight) */}
                    <div style={{ 
                        padding: '8px', 
                        backgroundColor: patientContext.x_studio_personal_illness ? '#fff3e0' : 'white', 
                        borderRadius: '6px', 
                        border: patientContext.x_studio_personal_illness ? '1px solid #ffb74d' : '1px solid #e0e0e0' 
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>🤒 Personal Illness:</div>
                        <div style={{ fontWeight: 'bold', color: patientContext.x_studio_personal_illness ? '#e65100' : '#333' }}>
                            {patientContext.x_studio_personal_illness || '-'}
                        </div>
                    </div>

                    {/* Drug Allergies (RED HIGHLIGHT) */}
                    <div style={{ 
                        padding: '8px', 
                        backgroundColor: (patientContext.x_studio_drug_allergies && patientContext.x_studio_drug_allergies !== 'ไม่มี') ? '#ffebee' : 'white', 
                        borderRadius: '6px', 
                        border: (patientContext.x_studio_drug_allergies && patientContext.x_studio_drug_allergies !== 'ไม่มี') ? '1px solid #ef5350' : '1px solid #e0e0e0' 
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>💊 Drug Allergies:</div>
                        <div style={{ fontWeight: 'bold', color: (patientContext.x_studio_drug_allergies && patientContext.x_studio_drug_allergies !== 'ไม่มี') ? '#c62828' : '#333' }}>
                            {patientContext.x_studio_drug_allergies || '-'}
                        </div>
                    </div>

                    {/* Food Allergies (RED HIGHLIGHT) */}
                    <div style={{ 
                        padding: '8px', 
                        backgroundColor: (patientContext.x_studio_food_allergies && patientContext.x_studio_food_allergies !== 'ไม่มี') ? '#ffebee' : 'white', 
                        borderRadius: '6px', 
                        border: (patientContext.x_studio_food_allergies && patientContext.x_studio_food_allergies !== 'ไม่มี') ? '1px solid #ef5350' : '1px solid #e0e0e0' 
                    }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>🍤 Food Allergies:</div>
                        <div style={{ fontWeight: 'bold', color: (patientContext.x_studio_food_allergies && patientContext.x_studio_food_allergies !== 'ไม่มี') ? '#c62828' : '#333' }}>
                            {patientContext.x_studio_food_allergies || '-'}
                        </div>
                    </div>
                </div>

                {/* === ROW 3: Birthdate | Career | Source | Email === */}
                <div style={rowStyle}>
                    {/* 🎂 Birthday & Age */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>🎂 Birthday & Age:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                            {(() => {
                                // 🎯 1. เครื่องแปลกาลเวลาแปลง AD -> BE
                                const bdate = patientContext.x_studio_birthdate;
                                let formattedDate = '-';
                                if (bdate && bdate.trim() !== "" && bdate !== "null" && bdate !== "-") {
                                    const parts = bdate.split('-');
                                    if (parts.length === 3) {
                                        formattedDate = `${parts[2]}/${parts[1]}/${parseInt(parts[0], 10) + 543}`;
                                    } else {
                                        formattedDate = bdate;
                                    }
                                }

                                // 🎯 2. สกัดสารพิษคำว่า "ปี" เบิ้ลซ้ำ (ฆ่าบั๊ก ปี ปี)
                                const age = patientContext.x_studio_age;
                                let formattedAge = '';
                                if (age && age !== "-") {
                                    const cleanAge = age.toString().replace(/ปี/g, '').trim();
                                    if (cleanAge) formattedAge = ` (${cleanAge} ปี)`;
                                }

                                return `${formattedDate}${formattedAge}`;
                            })()}
                        </div>
                    </div>

                    {/* 💼 Career */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>💼 Career:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                            {patientContext.x_studio_career && patientContext.x_studio_career !== 'undefined' ? patientContext.x_studio_career : '-'}
                        </div>
                    </div>

                    {/* 📢 Source */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>📢 Source:</div>
                        <div style={{ fontWeight: 'bold', color: '#333' }}>
                            {patientContext.x_studio_source_1 && patientContext.x_studio_source_1 !== 'undefined' ? patientContext.x_studio_source_1 : '-'}
                        </div>
                    </div>

                    {/* 📧 Email */}
                    <div style={{ padding: '8px', backgroundColor: 'white', borderRadius: '6px', border: '1px solid #e0e0e0' }}>
                        <div style={{ fontSize: '12px', color: '#666', marginBottom: '2px' }}>📧 Email:</div>
                        <div style={{ fontWeight: 'bold', color: '#333', wordBreak: 'break-all', fontSize: '12px' }}>
                            {patientContext.x_studio_email && patientContext.x_studio_email !== 'undefined' ? patientContext.x_studio_email : '-'}
                        </div>
                    </div>
                </div>
            </div>

            {/* ========== SECTION 2: PRIMARY ACTIONS ========== */}
            <div style={{ 
                display: 'flex',
                gap: '10px',
                marginBottom: '15px',
                flexWrap: 'wrap'
            }}>
                <button 
                    onClick={onShowVisitHistory}
                    style={{
                        padding: '12px 20px',
                        fontSize: '16px',
                        fontWeight: 'bold',
                        backgroundColor: '#2196F3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: 'pointer',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        flex: '1 1 auto'
                    }}
                >
                    <FaHistory /> Visit History / New Visit
                </button>

                {!isReadOnly && patientContext.mode !== 'history' && (
                    <button 
                        onClick={onShowTemplates}
                        style={{
                            padding: '12px 20px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: '#2196F3',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            flex: '1 1 auto'
                        }}
                    >
                        <FaFileAlt /> {showTemplates ? 'Hide' : 'Show'} Templates
                    </button>
                )}
            </div>
                
                {/* History Mode Notice */}
                {patientContext.mode === 'history' && (
                    <div style={{
                        padding: '12px 16px',
                        backgroundColor: '#fff3e0',
                        border: '1px solid #ff9800',
                        borderRadius: '6px',
                        fontSize: '14px',
                        color: '#e65100',
                        fontWeight: 'bold',
                        textAlign: 'center'
                    }}>
                        📖 History Mode: View existing visits only. Create new visits from Appointment view.
                    </div>
                )}

            {/* ========== SECTION 3: TEMPLATE SELECTION (if showing) ========== */}
            {showTemplates && !isReadOnly && (
                <div style={{ 
                    marginBottom: '15px', 
                    padding: '15px', 
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    border: '2px solid #ddd'
                }}>
                    <h3 style={{ marginTop: 0, marginBottom: '10px', fontSize: '16px' }}>Select Template:</h3>
                    <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
                        {templates
                        // 1. Filter out duplicates or hidden items
                        .filter((template, index, self) => {
                            // Hide items starting with '+' or having specific 'unused' names if you want
                            const isUnused = template.displayName.startsWith('+') || template.isUnused;
                            
                            // Only keep the first occurrence of a display name to remove duplicates
                            const isDuplicate = self.findIndex(t => t.displayName === template.displayName) !== index;
                            
                            return !isUnused && !isDuplicate;
                        })
                        // 2. Map the cleaned list
                        .map((template) => (
                            <div
                                key={template.name}
                                onClick={() => onLoadTemplate(template)}
                                style={{
                                    width: '120px',
                                    padding: '8px',
                                    border: selectedTemplate?.name === template.name ? '3px solid #2196F3' : '2px solid #ddd',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    backgroundColor: selectedTemplate?.name === template.name ? '#e3f2fd' : 'white',
                                    textAlign: 'center'
                                }}
                            >
                                <img 
                                    src={template.url} 
                                    alt={template.displayName}
                                    style={{ 
                                        width: '100%', 
                                        height: '90px', 
                                        objectFit: 'contain',
                                        marginBottom: '6px'
                                    }} 
                                />
                                {/* FIXED: Added notranslate to stop "Basil Leaf" error here too */}
                                <div 
                                    className="notranslate" 
                                    translate="no"
                                    style={{ fontWeight: 'bold', fontSize: '12px' }}
                                >
                                    {template.displayName}
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

            {/* ========== SECTION 4: CANVAS ACTIONS ========== */}
            {selectedTemplate && (
                <div style={{ 
                    display: 'flex',
                    gap: '10px',
                    marginBottom: '15px',
                    flexWrap: 'wrap'
                }}>
                    {!isReadOnly && (
                        <>
                            <button 
                                onClick={onClearAll}
                                style={{
                                    padding: '10px 18px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    backgroundColor: '#f44336',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaTrashAlt /> Clear All
                            </button>

                            <button 
                                onClick={onConfirmVisit}
                                style={{
                                    padding: '10px 18px',
                                    fontSize: '16px',
                                    fontWeight: 'bold',
                                    backgroundColor: 'transparent',
                                    color: '#f44336',
                                    border: '2px solid #f44336',
                                    borderRadius: '6px',
                                    cursor: 'pointer',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '6px'
                                }}
                            >
                                <FaLock /> Confirm & Lock
                            </button>
                        </>
                    )}

                    <span style={{ 
                        fontSize: '14px',
                        fontWeight: 'bold',
                        color: isSaving ? '#ff9800' : '#4CAF50',
                        display: 'flex',
                        alignItems: 'center',
                        padding: '0 10px'
                    }}>
                        {isSaving ? '💾 Saving...' : lastSaved ? `✅ Saved ${lastSaved.toLocaleTimeString()}` : ''}
                    </span>
                </div>
            )}

            {/* ========== SECTION 5: EXPORT OPTIONS ========== */}
            {selectedTemplate && (
                <div style={{
                    paddingTop: '15px',
                    borderTop: '1px solid #ddd',
                    display: 'flex',
                    gap: '10px',
                    flexWrap: 'wrap'
                }}>
                    <button
                        onClick={onExportPNG}
                        style={{
                            padding: '10px 18px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: '#4CAF50',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: '1 1 auto'
                        }}
                    >
                        <LuImage /> Export PNG
                    </button>

                    <button
                        onClick={onExportPDF}
                        style={{
                            padding: '10px 18px',
                            fontSize: '16px',
                            fontWeight: 'bold',
                            backgroundColor: '#FF5722',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            flex: '1 1 auto'
                        }}
                    >
                        <LuFileText /> Export PDF
                    </button>
                </div>
            )}

            {/* ========== SECTION 6: ZOOM CONTROLS ========== */}
            {selectedTemplate && (
                <div style={{
                    paddingTop: '15px',
                    borderTop: '1px solid #ddd',
                    marginTop: '15px',
                    display: 'flex',
                    gap: '10px',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexWrap: 'wrap'
                }}>
                    <button 
                        onClick={onZoomOut}
                        style={{
                            padding: '8px 14px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        🔍− Zoom Out
                    </button>

                    <span style={{ 
                        fontSize: '14px',
                        fontWeight: 'bold',
                        padding: '8px 14px',
                        backgroundColor: '#f5f5f5',
                        borderRadius: '6px',
                        border: '2px solid #ddd',
                        minWidth: '70px',
                        textAlign: 'center'
                    }}>
                        {zoomLevel}%
                    </span>

                    <button 
                        onClick={onZoomIn}
                        style={{
                            padding: '8px 14px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        🔍+ Zoom In
                    </button>

                    <button 
                        onClick={onResetZoom}
                        style={{
                            padding: '8px 14px',
                            fontSize: '14px',
                            fontWeight: 'bold',
                            backgroundColor: '#6c757d',
                            color: 'white',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px'
                        }}
                    >
                        ↺ Reset
                    </button>
                </div>
            )}
        </div>
    );
}

export default CollapsiblePatientInfo;